import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  DollarSign,
  ShoppingBag,
  Users,
  ArrowUp,
  Activity,
  CreditCard,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Wallet,
  Tag,
  X,
  Edit
} from 'lucide-react';
import { HitApi } from '../../Api/ApiHit';
import { searchBill, updateBill } from '../../constant/Constant';
import toast from 'react-hot-toast';

const GST_PERCENTAGE = 18;

const toNumber = (value) => Number(value) || 0;

const getItemsFinalTotal = (items = []) => {
  if (!Array.isArray(items)) return 0;

  return items.reduce((sum, item) => {
    const quantity = toNumber(item.quantity) || 1;
    const price = toNumber(item.pricing?.finalPrice || item.pricing?.totalPrice || item.price || item.totalPrice);
    return sum + (price * quantity);
  }, 0);
};

const getBillTaxableAmount = (bill) => {
  const totals = bill?.calculations?.totals || {};
  const calculationItems = bill?.calculations?.items || {};
  const servicesTotal = toNumber(calculationItems.services?.finalTotal) || getItemsFinalTotal(bill?.services);
  const productsTotal = toNumber(calculationItems.products?.finalTotal) || getItemsFinalTotal(bill?.products);
  const taxableItemsTotal = servicesTotal + productsTotal;

  if (taxableItemsTotal <= 0) return 0;

  const subtotalAfterItemDiscount = toNumber(totals.subtotalAfterItemDiscount) || taxableItemsTotal;
  const couponDiscount = toNumber(totals.couponDiscount);
  const taxableCouponDiscount = subtotalAfterItemDiscount > 0
    ? (couponDiscount * taxableItemsTotal) / subtotalAfterItemDiscount
    : 0;
  const gstInclusiveAmount = Math.max(0, taxableItemsTotal - taxableCouponDiscount);

  return (gstInclusiveAmount * 100) / (100 + GST_PERCENTAGE);
};

function Dashboard() {
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState('today');
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [billsData, setBillsData] = useState([]);
  const [pendingBillsData, setPendingBillsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPending, setIsLoadingPending] = useState(false);
  const [billsToShow, setBillsToShow] = useState(10);
  const [showTodayPendingOnly, setShowTodayPendingOnly] = useState(false);
  const [showAllPendingBills, setShowAllPendingBills] = useState(false);
  const [cancellingBillId, setCancellingBillId] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({
    totalBills: 0,
    totalRevenue: 0,
    taxableRevenue: 0,
    paidRevenue: 0,
    pendingAmount: 0,
    serviceRevenue: 0,
    productRevenue: 0,
    membershipRevenue: 0,
    walletPayment: 0,
    totalDiscount: 0,
    totalClients: 0,
    averageBill: 0,
    paidBills: 0,
    pendingBills: 0,
    totalServices: 0,
    totalProducts: 0,
    newCustomers: 0,
    repeatCustomers: 0
  });

  const filterOptions = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'This Week', value: 'thisWeek' },
    { label: 'This Month', value: 'thisMonth' },
    { label: 'Last 7 Days', value: 'last7Days' },
    { label: 'Last 30 Days', value: 'last30Days' },
    { label: 'Custom Range', value: 'customRange' },
  ];

  const handleFilterChange = (value) => {
    setSelectedFilter(value);
    setBillsToShow(10); // Reset to 10 when filter changes
    if (value === 'customRange') {
      setShowCustomRange(true);
    } else {
      setShowCustomRange(false);
      setStartDate('');
      setEndDate('');
    }
  };

  const handleShowMore = () => {
    setBillsToShow(prev => prev + 10);
  };

  const handleShowLess = () => {
    setBillsToShow(10);
    // Scroll to table top
    document.querySelector('.bills-table')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCompleteBill = (bill) => {
    // Navigate to autoBilling edit page to complete the payment
    navigate(`/autoBilling/edit/${bill._id}`);
  };

  const handleEditAppointment = (bill) => {
    // Navigate to appointments edit page
    navigate(`/appoinments/edit/${bill._id}`);
  };

  const handleCancelAppointment = async (bill) => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to cancel this appointment?\n\n` +
      `Bill Number: ${bill.billNumber}\n` +
      `Client: ${bill.client?.name || 'N/A'}\n` +
      `Amount: ₹${bill.calculations?.totals?.grandTotal || 0}\n\n` +
      `This action will mark the appointment as cancelled.`
    );

    if (!confirmed) return;

    setCancellingBillId(bill._id);

    try {
      // Update bill status to cancelled
      const updateData = {
        _id: bill._id,
        status: 'cancelled',
        billStatus: 'CANCELLED'
      };

      const res = await HitApi(updateData, updateBill);

      if (res?.statusCode === 200) {
        toast.success('Appointment cancelled successfully');

        // Refresh pending bills to remove the cancelled one
        fetchPendingBills();

        // Also refresh main bills data if needed
        fetchBillsData();
      } else {
        toast.error(res?.message || 'Failed to cancel appointment');
      }
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error('Error cancelling appointment. Please try again.');
    } finally {
      setCancellingBillId(null);
    }
  };

  const handleApplyCustomRange = () => {
    if (startDate && endDate) {
      console.log('Custom range applied:', startDate, 'to', endDate);
      fetchBillsData();
      fetchPendingBills(); // Refresh pending bills as well
    }
  };

  // Fetch pending bills (all pending bills, no date filter)
  const fetchPendingBills = async () => {
    setIsLoadingPending(true);

    try {
      const searchObj = {
        'payment.paymentStatus': { $ne: 'Paid' },
        status: { $ne: 'cancelled' }
      };

      const json = {
        page: 1,
        limit: 10000,
        search: searchObj,
        sort: { createdAt: -1 } // Most recent first
      };

      console.log('📋 Fetching pending bills with query:', json);

      const res = await HitApi(json, searchBill);

      if (res?.statusCode === 200) {
        const bills = Array.isArray(res?.data?.docs) ? res?.data?.docs : [];
        console.log('📋 Pending bills fetched:', bills.length, 'bills');
        console.log('📋 Sample pending bill:', bills[0]);
        setPendingBillsData(bills);
      } else {
        console.error('📋 Failed to fetch pending bills:', res);
        setPendingBillsData([]);
      }
    } catch (error) {
      console.error('📋 Error fetching pending bills:', error);
      setPendingBillsData([]);
    } finally {
      setIsLoadingPending(false);
    }
  };

  // Fetch bills data based on selected filter
  const fetchBillsData = async () => {
    setIsLoading(true);

    try {
      const { start, end } = getDateRangeTimestamps();

      const searchObj = {
        createdAt: {
          $gte: start,
          $lte: end
        }
      };

      const json = {
        page: 1,
        limit: 10000,
        search: searchObj
      };

      console.log('Fetching bills with:', json);

      const res = await HitApi(json, searchBill);

      if (res?.statusCode === 200) {
        const bills = Array.isArray(res?.data?.docs) ? res?.data?.docs : [];
        setBillsData(bills);
        calculateDashboardStats(bills);
        toast.success(`Loaded ${bills.length} bills`);
      } else {
        setBillsData([]);
        setDashboardStats({
          totalBills: 0,
          totalRevenue: 0,
          taxableRevenue: 0,
          paidRevenue: 0,
          pendingAmount: 0,
          serviceRevenue: 0,
          productRevenue: 0,
          membershipRevenue: 0,
          walletPayment: 0,
          totalClients: 0,
          averageBill: 0,
          paidBills: 0,
          pendingBills: 0,
          totalServices: 0,
          totalProducts: 0,
          newCustomers: 0,
          repeatCustomers: 0
        });
        toast.error('Failed to fetch bills');
      }
    } catch (error) {
      console.error('Error fetching bills:', error);
      toast.error('Error loading dashboard data');
      setBillsData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Get timestamps for the selected filter
  const getDateRangeTimestamps = () => {
    const now = new Date();
    let start, end;

    switch (selectedFilter) {
      case 'today':
        start = new Date(now).setHours(0, 0, 0, 0);
        end = new Date(now).setHours(23, 59, 59, 999);
        break;

      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        start = yesterday.setHours(0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).setHours(23, 59, 59, 999);
        break;

      case 'thisWeek':
        const weekStart = new Date(now);
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1);
        weekStart.setDate(diff);
        start = weekStart.setHours(0, 0, 0, 0);
        end = new Date(now).setHours(23, 59, 59, 999);
        break;

      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1).setHours(0, 0, 0, 0);
        end = new Date(now).setHours(23, 59, 59, 999);
        break;

      case 'last7Days':
        const last7 = new Date(now);
        last7.setDate(now.getDate() - 7);
        start = last7.setHours(0, 0, 0, 0);
        end = new Date(now).setHours(23, 59, 59, 999);
        break;

      case 'last30Days':
        const last30 = new Date(now);
        last30.setDate(now.getDate() - 30);
        start = last30.setHours(0, 0, 0, 0);
        end = new Date(now).setHours(23, 59, 59, 999);
        break;

      case 'customRange':
        if (startDate && endDate) {
          start = new Date(startDate).setHours(0, 0, 0, 0);
          end = new Date(endDate).setHours(23, 59, 59, 999);
        } else {
          start = new Date(now).setHours(0, 0, 0, 0);
          end = new Date(now).setHours(23, 59, 59, 999);
        }
        break;

      default:
        start = new Date(now).setHours(0, 0, 0, 0);
        end = new Date(now).setHours(23, 59, 59, 999);
    }

    return { start, end };
  };

  // Calculate dashboard statistics from bills data
  const calculateDashboardStats = (bills) => {
    if (!bills || bills.length === 0) {
      // Still calculate pending amount even if no bills in current filter
      console.log('💰 No bills in filter, calculating pending from', pendingBillsData.length, 'pending bills');
      const pendingAmount = pendingBillsData.reduce((sum, bill, index) => {
        const totalAmount = bill.calculations?.totals?.grandTotal || 0;
        const paidAmount = bill.payment?.totalPaid || 0;
        const remainingAmount = totalAmount - paidAmount;

        if (index < 3) {
          console.log(`💰 Pending Bill ${index + 1}:`, {
            billNumber: bill.billNumber,
            totalAmount,
            paidAmount,
            remainingAmount,
            paymentStatus: bill.payment?.paymentStatus
          });
        }

        return sum + remainingAmount;
      }, 0);

      console.log('💰 Pending amount (no filter bills):', pendingAmount);

      setDashboardStats({
        totalBills: 0,
        totalRevenue: 0,
        taxableRevenue: 0,
        paidRevenue: 0,
        pendingAmount: pendingAmount,
        serviceRevenue: 0,
        productRevenue: 0,
        membershipRevenue: 0,
        walletPayment: 0,
        totalClients: 0,
        averageBill: 0,
        paidBills: 0,
        pendingBills: pendingBillsData.length,
        totalServices: 0,
        totalProducts: 0,
        newCustomers: 0,
        repeatCustomers: 0
      });
      return;
    }

    const totalBills = bills.length;

    // Calculate wallet payment (only from paid bills)
    const walletPayment = bills
      .filter(bill => bill.payment?.paymentStatus === 'Paid')
      .reduce((sum, bill) => {
        return sum + (bill.payment?.methods?.wallet || 0);
      }, 0);

    // Calculate total revenue (only from paid bills, grand total minus wallet payment)
    const totalRevenue = bills
      .filter(bill => bill.payment?.paymentStatus === 'Paid')
      .reduce((sum, bill) => {
        const grandTotal = bill.calculations?.totals?.grandTotal || 0;
        const walletAmount = bill.payment?.methods?.wallet || 0;
        return sum + (grandTotal - walletAmount);
      }, 0);

    // Service and product prices are GST-inclusive. Match BillMaster export taxable amount.
    const taxableRevenue = bills
      .filter(bill => bill.payment?.paymentStatus === 'Paid')
      .reduce((sum, bill) => {
        return sum + getBillTaxableAmount(bill);
      }, 0);

    // Calculate paid revenue (only from paid bills, minus wallet payment)
    const paidRevenue = bills
      .filter(bill => bill.payment?.paymentStatus === 'Paid')
      .reduce((sum, bill) => {
        const grandTotal = bill.calculations?.totals?.grandTotal || 0;
        const walletAmount = bill.payment?.methods?.wallet || 0;
        return sum + (grandTotal - walletAmount);
      }, 0);

    // Calculate pending amount from ALL pending bills (from pendingBillsData)
    // This includes both unpaid and partially paid bills, excluding wallet payments
    console.log('💰 Calculating pending amount from', pendingBillsData.length, 'pending bills');
    const pendingAmount = pendingBillsData.reduce((sum, bill, index) => {
      const totalAmount = bill.calculations?.totals?.grandTotal || 0;
      const paidAmount = bill.payment?.totalPaid || 0;
      const walletAmount = bill.payment?.methods?.wallet || 0;
      // Remaining = (Grand Total - Wallet Payment) - (Paid Amount - Wallet Payment)
      const remainingAmount = (totalAmount - walletAmount) - (paidAmount - walletAmount);

      if (index < 3) { // Log first 3 bills for debugging
        console.log(`💰 Bill ${index + 1}:`, {
          billNumber: bill.billNumber,
          totalAmount,
          paidAmount,
          walletAmount,
          remainingAmount,
          paymentStatus: bill.payment?.paymentStatus,
          calculationsPath: bill.calculations?.totals
        });
      }

      return sum + remainingAmount;
    }, 0);

    console.log('💰 Total pending amount calculated:', pendingAmount);

    // Calculate membership revenue (only from paid bills, minus proportional wallet payment)
    const membershipRevenue = bills
      .filter(bill => bill.payment?.paymentStatus === 'Paid')
      .reduce((sum, bill) => {
        const membershipFinalTotal = bill.calculations?.items?.memberships?.finalTotal || 0;
        const grandTotal = bill.calculations?.totals?.grandTotal || 0;
        const walletAmount = bill.payment?.methods?.wallet || 0;

        // Calculate proportional wallet payment for memberships
        const membershipWalletPortion = grandTotal > 0 ? (membershipFinalTotal / grandTotal) * walletAmount : 0;
        return sum + (membershipFinalTotal - membershipWalletPortion);
      }, 0);

    // Calculate service revenue (only from paid bills, minus proportional wallet payment)
    const serviceRevenue = bills
      .filter(bill => bill.payment?.paymentStatus === 'Paid')
      .reduce((sum, bill) => {
        const serviceFinalTotal = bill.calculations?.items?.services?.finalTotal || 0;
        const grandTotal = bill.calculations?.totals?.grandTotal || 0;
        const walletAmount = bill.payment?.methods?.wallet || 0;

        // Calculate proportional wallet payment for services
        const serviceWalletPortion = grandTotal > 0 ? (serviceFinalTotal / grandTotal) * walletAmount : 0;
        return sum + (serviceFinalTotal - serviceWalletPortion);
      }, 0);

    // Calculate product revenue (only from paid bills, minus proportional wallet payment)
    const productRevenue = bills
      .filter(bill => bill.payment?.paymentStatus === 'Paid')
      .reduce((sum, bill) => {
        const productFinalTotal = bill.calculations?.items?.products?.finalTotal || 0;
        const grandTotal = bill.calculations?.totals?.grandTotal || 0;
        const walletAmount = bill.payment?.methods?.wallet || 0;

        // Calculate proportional wallet payment for products
        const productWalletPortion = grandTotal > 0 ? (productFinalTotal / grandTotal) * walletAmount : 0;
        return sum + (productFinalTotal - productWalletPortion);
      }, 0);

    // Calculate total discount given (only from paid bills)
    const totalDiscount = bills
      .filter(bill => bill.payment?.paymentStatus === 'Paid')
      .reduce((sum, bill) => {
        const serviceDiscount = bill.calculations?.items?.services?.totalDiscount || 0;
        const productDiscount = bill.calculations?.items?.products?.totalDiscount || 0;
        const membershipDiscount = bill.calculations?.items?.memberships?.totalDiscount || 0;
        return sum + serviceDiscount + productDiscount + membershipDiscount;
      }, 0);

    const uniqueClients = new Set();
    const clientDataMap = new Map(); // Store client data
    let newCustomersCount = 0;
    let repeatCustomersCount = 0;

    bills.forEach(bill => {
      if (bill.client?._id) {
        const clientId = bill.client._id;
        uniqueClients.add(clientId);

        // Store client data if not already stored
        if (!clientDataMap.has(clientId)) {
          clientDataMap.set(clientId, bill.client);
        }
      }
    });

    // Get date range for the current filter
    const { start: filterStartDate, end: filterEndDate } = getDateRangeTimestamps();

    // Count new vs repeat customers based on creation date
    uniqueClients.forEach(clientId => {
      const client = clientDataMap.get(clientId);

      if (client && client.createdAt) {
        const clientCreatedDate = new Date(client.createdAt).getTime();

        // New customer: created within the selected date range
        // Repeat customer: created before the selected date range
        if (clientCreatedDate >= filterStartDate && clientCreatedDate <= filterEndDate) {
          newCustomersCount++;
        } else {
          repeatCustomersCount++;
        }
      } else {
        // If no creation date, count as repeat (safer assumption)
        repeatCustomersCount++;
      }
    });

    const paidBills = bills.filter(bill => bill.payment?.paymentStatus === 'Paid').length;

    const averageBill = paidBills > 0 ? totalRevenue / paidBills : 0;

    const totalServices = bills
      .filter(bill => bill.payment?.paymentStatus === 'Paid')
      .reduce((sum, bill) => {
        return sum + (bill.services?.length || 0);
      }, 0);

    const totalProducts = bills
      .filter(bill => bill.payment?.paymentStatus === 'Paid')
      .reduce((sum, bill) => {
        return sum + (bill.products?.length || 0);
      }, 0);

    setDashboardStats({
      totalBills,
      totalRevenue,
      taxableRevenue,
      paidRevenue,
      pendingAmount,
      serviceRevenue,
      productRevenue,
      membershipRevenue,
      walletPayment,
      totalDiscount,
      totalClients: uniqueClients.size,
      averageBill,
      paidBills,
      pendingBills: pendingBillsData.length, // Total pending bills from separate API
      totalServices,
      totalProducts,
      newCustomers: newCustomersCount,
      repeatCustomers: repeatCustomersCount
    });
  };

  // Fetch data when filter changes
  useEffect(() => {
    if (selectedFilter !== 'customRange') {
      fetchBillsData();
    }
  }, [selectedFilter]);

  // Fetch pending bills on component mount and when returning to dashboard
  useEffect(() => {
    fetchPendingBills();
    fetchBillsData();

    // Set up interval to refresh pending bills every 30 seconds
    const interval = setInterval(() => {
      fetchPendingBills();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Recalculate stats when pending bills data changes
  useEffect(() => {
    if (billsData.length > 0) {
      calculateDashboardStats(billsData);
    } else {
      // Even with no bills in current filter, show pending amount
      calculateDashboardStats([]);
    }
  }, [pendingBillsData]);

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0';
    return `₹${Math.round(amount).toLocaleString()}`;
  };

  // Filter pending bills based on toggle
  const getFilteredPendingBills = () => {
    let filteredBills = [];

    if (!showTodayPendingOnly) {
      filteredBills = pendingBillsData;
    } else {
      // Filter to show only today's appointments
      const todayStart = new Date().setHours(0, 0, 0, 0);
      const todayEnd = new Date().setHours(23, 59, 59, 999);

      filteredBills = pendingBillsData.filter(bill => {
        // Use appointment date (bill.date) instead of creation date
        const appointmentDate = bill.date || new Date(bill.timestamp || bill.createdAt).getTime();
        return appointmentDate >= todayStart && appointmentDate <= todayEnd;
      });
    }

    // Return in reverse order (most recent appointment first)
    return [...filteredBills].reverse();
  };

  const getDateRangeText = () => {
    const now = new Date();

    switch (selectedFilter) {
      case 'today':
        return now.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });

      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        return yesterday.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });

      case 'thisWeek':
        const weekStart = new Date(now);
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1);
        weekStart.setDate(diff);
        return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

      case 'thisMonth':
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return `${monthStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;

      case 'last7Days':
        const last7 = new Date(now);
        last7.setDate(now.getDate() - 7);
        return `${last7.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

      case 'last30Days':
        const last30 = new Date(now);
        last30.setDate(now.getDate() - 30);
        return `${last30.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

      case 'customRange':
        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);
          return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        }
        return 'Select custom date range';

      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen p-5">
      {/* Header Section */}
      <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 backdrop-blur-lg bg-opacity-90">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-3 rounded-xl shadow-lg">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Dashboard
                </h1>
                <p className="text-sm text-gray-500">Welcome back! Here's your business overview</p>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3 bg-blue-50 px-4 py-2 rounded-lg border border-blue-100">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="font-medium text-blue-700 text-sm">{getDateRangeText()}</span>
            </div>
          </div>

          {/* Filter Dropdown and Refresh Button */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                fetchBillsData();
                fetchPendingBills();
                toast.success('Dashboard refreshed');
              }}
              disabled={isLoading || isLoadingPending}
              className="p-2.5 border-2 border-gray-200 rounded-xl bg-white text-gray-700 hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh dashboard"
            >
              <RefreshCw className={`w-5 h-5 ${(isLoading || isLoadingPending) ? 'animate-spin' : ''}`} />
            </button>
            <select
              value={selectedFilter}
              onChange={(e) => handleFilterChange(e.target.value)}
              className="px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-700 font-medium cursor-pointer shadow-sm hover:border-blue-300 transition-all"
            >
              {filterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Custom Date Range Picker */}
        {showCustomRange && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-4 mt-4 shadow-inner">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-semibold text-gray-700">Custom Date Range:</span>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 font-medium">From:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
                />
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 font-medium">To:</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
                />
              </div>

              <button
                onClick={handleApplyCustomRange}
                disabled={!startDate || !endDate}
                className={`px-6 py-2 rounded-lg font-semibold transition-all shadow-md ${startDate && endDate
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg transform hover:-translate-y-0.5'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
              >
                Apply Filter
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex flex-col justify-center items-center py-20">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
            <div className="absolute top-0 left-0 animate-ping rounded-full h-16 w-16 border-b-4 border-purple-400 opacity-75"></div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">Loading dashboard data...</p>
        </div>
      )}

      {/* Dashboard Content */}
      {!isLoading && (
        <>
          {/* Main Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            {/* Paid Revenue Card */}
            <div className="group relative bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl shadow-lg p-4 overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full -mr-12 -mt-12"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <div className="bg-white bg-opacity-20 p-2 rounded-lg backdrop-blur-sm">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex items-center gap-1 bg-white bg-opacity-20 px-1.5 py-0.5 rounded-full">
                    <CheckCircle className="w-3 h-3 text-white" />
                  </div>
                </div>
                <h3 className="text-white text-opacity-90 text-xs font-medium mb-1">Paid Revenue</h3>
                <p className="text-xl font-bold text-white mb-0.5">{formatCurrency(dashboardStats.paidRevenue)}</p>
                <p className="text-white text-opacity-75 text-[10px]">Collected payments</p>
              </div>
            </div>

            {/* Taxable Revenue Card */}
            <div className="group relative bg-gradient-to-br from-cyan-600 to-blue-700 rounded-xl shadow-lg p-4 overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full -mr-12 -mt-12"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <div className="bg-white bg-opacity-20 p-2 rounded-lg backdrop-blur-sm">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex items-center gap-1 bg-white bg-opacity-20 px-1.5 py-0.5 rounded-full">
                    <Activity className="w-3 h-3 text-white" />
                  </div>
                </div>
                <h3 className="text-white text-opacity-90 text-xs font-medium mb-1">Taxable Revenue</h3>
                <p className="text-xl font-bold text-white mb-0.5">{formatCurrency(dashboardStats.taxableRevenue)}</p>
                <p className="text-white text-opacity-75 text-[10px]">Before 18% GST</p>
              </div>
            </div>

            {/* Pending Amount Card */}
            <div className="group relative bg-gradient-to-br from-orange-500 to-red-600 rounded-xl shadow-lg p-4 overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full -mr-12 -mt-12"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <div className="bg-white bg-opacity-20 p-2 rounded-lg backdrop-blur-sm">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex items-center gap-1 bg-white bg-opacity-20 px-1.5 py-0.5 rounded-full">
                    <AlertCircle className="w-3 h-3 text-white" />
                  </div>
                </div>
                <h3 className="text-white text-opacity-90 text-xs font-medium mb-1">Pending Amount</h3>
                <p className="text-xl font-bold text-white mb-0.5">{formatCurrency(dashboardStats.pendingAmount)}</p>
                <p className="text-white text-opacity-75 text-[10px]">{dashboardStats.pendingBills} bills pending</p>
              </div>
            </div>

            {/* Total Bills Card */}
            <div className="group relative bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg p-4 overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full -mr-12 -mt-12"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <div className="bg-white bg-opacity-20 p-2 rounded-lg backdrop-blur-sm">
                    <ShoppingBag className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex items-center gap-1 bg-white bg-opacity-20 px-1.5 py-0.5 rounded-full">
                    <Activity className="w-3 h-3 text-white" />
                  </div>
                </div>
                <h3 className="text-white text-opacity-90 text-xs font-medium mb-1">Total Bills</h3>
                <p className="text-xl font-bold text-white mb-0.5">{dashboardStats.totalBills}</p>
                <p className="text-white text-opacity-75 text-[10px]">{dashboardStats.paidBills} paid, {dashboardStats.pendingBills} pending</p>
              </div>
            </div>

            {/* New vs Repeat Customers Card */}
            <div className="group relative bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl shadow-lg p-4 overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full -mr-12 -mt-12"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-2">
                  <div className="bg-white bg-opacity-20 p-2 rounded-lg backdrop-blur-sm">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex items-center gap-1 bg-white bg-opacity-20 px-1.5 py-0.5 rounded-full">
                    <Activity className="w-3 h-3 text-white" />
                  </div>
                </div>
                <h3 className="text-white text-opacity-90 text-xs font-medium mb-1">Customer Type</h3>
                <p className="text-xl font-bold text-white mb-0.5">New/Repeat {dashboardStats.newCustomers}/{dashboardStats.repeatCustomers}</p>
                <p className="text-white text-opacity-75 text-[10px]">
                  {dashboardStats.totalClients > 0 && dashboardStats.newCustomers > 0 && (
                    <span>{((dashboardStats.newCustomers / dashboardStats.totalClients) * 100).toFixed(0)}% new • </span>
                  )}
                  {dashboardStats.totalClients > 0 && dashboardStats.repeatCustomers > 0 && (
                    <span>{((dashboardStats.repeatCustomers / dashboardStats.totalClients) * 100).toFixed(0)}% repeat</span>
                  )}
                  {dashboardStats.totalClients === 0 && <span>No customers yet</span>}
                </p>
              </div>
            </div>

          </div>

          {/* Secondary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-9 gap-4 mb-6">
            {/* Services Card */}
            <div className="group relative bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg shadow-md p-3 overflow-hidden transform transition-all hover:shadow-xl hover:-translate-y-1 border border-blue-200">
              <div className="absolute top-0 right-0 w-16 h-16 bg-blue-200 opacity-20 rounded-full -mr-8 -mt-8"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="bg-gradient-to-br from-blue-500 to-indigo-600 p-1.5 rounded-lg shadow-md">
                    <Activity className="w-4 h-4 text-white" />
                  </div>
                </div>
                <p className="text-gray-600 text-[10px] font-medium mb-0.5">Total Services</p>
                <p className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">{dashboardStats.totalServices}</p>
              </div>
            </div>

            {/* Products Card */}
            <div className="group relative bg-gradient-to-br from-green-50 to-emerald-100 rounded-lg shadow-md p-3 overflow-hidden transform transition-all hover:shadow-xl hover:-translate-y-1 border border-green-200">
              <div className="absolute top-0 right-0 w-16 h-16 bg-green-200 opacity-20 rounded-full -mr-8 -mt-8"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-1.5 rounded-lg shadow-md">
                    <ShoppingBag className="w-4 h-4 text-white" />
                  </div>
                </div>
                <p className="text-gray-600 text-[10px] font-medium mb-0.5">Total Products</p>
                <p className="text-xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">{dashboardStats.totalProducts}</p>
              </div>
            </div>

            {/* Paid Bills Card */}
            <div className="group relative bg-gradient-to-br from-emerald-50 to-teal-100 rounded-lg shadow-md p-3 overflow-hidden transform transition-all hover:shadow-xl hover:-translate-y-1 border border-emerald-200">
              <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-200 opacity-20 rounded-full -mr-8 -mt-8"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-1.5 rounded-lg shadow-md">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                </div>
                <p className="text-gray-600 text-[10px] font-medium mb-0.5">Paid Bills</p>
                <p className="text-xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">{dashboardStats.paidBills}</p>
              </div>
            </div>

            {/* Pending Bills Card */}
            <div className="group relative bg-gradient-to-br from-orange-50 to-amber-100 rounded-lg shadow-md p-3 overflow-hidden transform transition-all hover:shadow-xl hover:-translate-y-1 border border-orange-200">
              <div className="absolute top-0 right-0 w-16 h-16 bg-orange-200 opacity-20 rounded-full -mr-8 -mt-8"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-1.5 rounded-lg shadow-md">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                </div>
                <p className="text-gray-600 text-[10px] font-medium mb-0.5">Pending Bills</p>
                <p className="text-xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">{dashboardStats.pendingBills}</p>
              </div>
            </div>

            {/* Service Revenue Card */}
            <div className="group relative bg-gradient-to-br from-cyan-50 to-sky-100 rounded-lg shadow-md p-3 overflow-hidden transform transition-all hover:shadow-xl hover:-translate-y-1 border border-cyan-200">
              <div className="absolute top-0 right-0 w-16 h-16 bg-cyan-200 opacity-20 rounded-full -mr-8 -mt-8"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="bg-gradient-to-br from-cyan-500 to-sky-600 p-1.5 rounded-lg shadow-md">
                    <Activity className="w-4 h-4 text-white" />
                  </div>
                </div>
                <p className="text-gray-600 text-[10px] font-medium mb-0.5">Service Revenue</p>
                <p className="text-lg font-bold bg-gradient-to-r from-cyan-600 to-sky-600 bg-clip-text text-transparent">{formatCurrency(dashboardStats.serviceRevenue)}</p>
              </div>
            </div>

            {/* Product Revenue Card */}
            <div className="group relative bg-gradient-to-br from-pink-50 to-rose-100 rounded-lg shadow-md p-3 overflow-hidden transform transition-all hover:shadow-xl hover:-translate-y-1 border border-pink-200">
              <div className="absolute top-0 right-0 w-16 h-16 bg-pink-200 opacity-20 rounded-full -mr-8 -mt-8"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="bg-gradient-to-br from-pink-500 to-rose-600 p-1.5 rounded-lg shadow-md">
                    <ShoppingBag className="w-4 h-4 text-white" />
                  </div>
                </div>
                <p className="text-gray-600 text-[10px] font-medium mb-0.5">Product Revenue</p>
                <p className="text-lg font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">{formatCurrency(dashboardStats.productRevenue)}</p>
              </div>
            </div>

            {/* Membership Revenue Card */}
            <div className="group relative bg-gradient-to-br from-purple-50 to-violet-100 rounded-lg shadow-md p-3 overflow-hidden transform transition-all hover:shadow-xl hover:-translate-y-1 border border-purple-200">
              <div className="absolute top-0 right-0 w-16 h-16 bg-purple-200 opacity-20 rounded-full -mr-8 -mt-8"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="bg-gradient-to-br from-purple-500 to-violet-600 p-1.5 rounded-lg shadow-md">
                    <Users className="w-4 h-4 text-white" />
                  </div>
                </div>
                <p className="text-gray-600 text-[10px] font-medium mb-0.5">Membership</p>
                <p className="text-lg font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">{formatCurrency(dashboardStats.membershipRevenue)}</p>
              </div>
            </div>

            {/* Wallet Payment Card */}
            <div className="group relative bg-gradient-to-br from-amber-50 to-yellow-100 rounded-lg shadow-md p-3 overflow-hidden transform transition-all hover:shadow-xl hover:-translate-y-1 border border-amber-200">
              <div className="absolute top-0 right-0 w-16 h-16 bg-amber-200 opacity-20 rounded-full -mr-8 -mt-8"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="bg-gradient-to-br from-amber-500 to-yellow-600 p-1.5 rounded-lg shadow-md">
                    <Wallet className="w-4 h-4 text-white" />
                  </div>
                </div>
                <p className="text-gray-600 text-[10px] font-medium mb-0.5">Wallet Payment</p>
                <p className="text-lg font-bold bg-gradient-to-r from-amber-600 to-yellow-600 bg-clip-text text-transparent">{formatCurrency(dashboardStats.walletPayment)}</p>
              </div>
            </div>

            {/* Total Discount Card */}
            <div className="group relative bg-gradient-to-br from-rose-50 to-pink-100 rounded-lg shadow-md p-3 overflow-hidden transform transition-all hover:shadow-xl hover:-translate-y-1 border border-rose-200">
              <div className="absolute top-0 right-0 w-16 h-16 bg-rose-200 opacity-20 rounded-full -mr-8 -mt-8"></div>
              <div className="relative">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="bg-gradient-to-br from-rose-500 to-pink-600 p-1.5 rounded-lg shadow-md">
                    <Tag className="w-4 h-4 text-white" />
                  </div>
                </div>
                <p className="text-gray-600 text-[10px] font-medium mb-0.5">Total Discount</p>
                <p className="text-lg font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">{formatCurrency(dashboardStats.totalDiscount)}</p>
              </div>
            </div>
          </div>

          {/* Pending Bills Card */}
          {isLoadingPending ? (
            <div className="bg-white rounded-2xl shadow-xl p-12 mb-6 text-center">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-orange-600 mb-4"></div>
                <p className="text-gray-600 font-medium">Loading pending bills...</p>
              </div>
            </div>
          ) : pendingBillsData.length > 0 && (
            <div className="pending-bills-section bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl shadow-xl p-6 mb-6 border-2 border-orange-200">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-r from-orange-500 to-red-500 p-3 rounded-xl shadow-lg">
                    <AlertCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">
                      {showTodayPendingOnly ? "Today's Pending Appointments" : "All Pending Bills"}
                    </h2>
                    <p className="text-sm text-gray-600">
                      {showTodayPendingOnly
                        ? "Appointments scheduled for today requiring payment"
                        : "All bills requiring payment (sorted by appointment date)"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {/* Toggle Button */}
                  <button
                    onClick={() => {
                      setShowTodayPendingOnly(!showTodayPendingOnly);
                      setShowAllPendingBills(false); // Reset to show only 6 bills when toggling
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 ${showTodayPendingOnly
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                        : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-orange-400'
                      }`}
                    title={showTodayPendingOnly ? 'Show all pending bills' : 'Show only appointments scheduled for today'}
                  >
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">{showTodayPendingOnly ? 'Show All' : "Today's Appts"}</span>
                  </button>

                  <div className="flex items-center gap-2 bg-orange-100 px-4 py-2 rounded-lg border border-orange-200">
                    <Clock className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-bold text-orange-700">
                      {getFilteredPendingBills().length} Pending
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getFilteredPendingBills()
                  .slice(0, showAllPendingBills ? getFilteredPendingBills().length : 6)
                  .map((bill, index) => (
                    <div
                      key={bill._id || index}
                      className={`bg-white rounded-xl shadow-md p-4 border-l-4 hover:shadow-lg transition-all transform hover:-translate-y-1 ${bill.status === 'cancelled' || bill.status === 'canceled'
                          ? 'border-gray-400 opacity-75'
                          : 'border-orange-500'
                        }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`p-2 rounded-lg ${bill.billType === 'APPOINTMENT'
                              ? 'bg-blue-100'
                              : 'bg-orange-100'
                            }`}>
                            {bill.billType === 'APPOINTMENT' ? (
                              <Calendar className="w-4 h-4 text-blue-600" />
                            ) : (
                              <CreditCard className="w-4 h-4 text-orange-600" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <p className="text-sm font-bold text-gray-900">{bill.billNumber || 'N/A'}</p>
                              {bill.billType === 'APPOINTMENT' && (
                                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[9px] font-bold rounded uppercase">
                                  Appt
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              {bill.billDate ? bill.billDate : new Date(bill.date || bill.timestamp).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </p>
                            {bill.billTime && (
                              <p className="text-[10px] text-gray-400">
                                {bill.billTime}
                              </p>
                            )}
                          </div>
                        </div>
                        <span className={`px-2 py-1 text-xs font-bold rounded-full ${bill.status === 'cancelled'
                            ? 'bg-gray-200 text-gray-700'
                            : bill.payment?.paymentStatus === 'Partial'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                          {bill.status === 'cancelled' ? 'Cancelled' : (bill.payment?.paymentStatus || 'Unpaid')}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 mb-3">
                        <div className="bg-purple-100 p-1.5 rounded-full">
                          <Users className="w-3 h-3 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{bill.client?.name || 'N/A'}</p>
                          <p className="text-xs text-gray-500">{bill.client?.phoneNumber || ''}</p>
                        </div>
                      </div>

                      <div className="border-t border-gray-200 pt-3 mt-3">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs text-gray-600 font-medium">Amount Due:</span>
                          <span className="text-lg font-bold text-orange-600">
                            {formatCurrency(bill.calculations?.totals?.grandTotal || 0)}
                          </span>
                        </div>
                        {bill.payment?.totalPaid > 0 && (
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs text-gray-600">Paid:</span>
                            <span className="text-sm font-semibold text-green-600">
                              {formatCurrency(bill.payment.totalPaid)}
                            </span>
                          </div>
                        )}

                        {/* Action Buttons */}
                        {bill.status !== 'cancelled' && bill.status !== 'canceled' ? (
                          <div className="space-y-2">
                            {/* Edit Button - Full Width */}
                            <button
                              onClick={() => handleEditAppointment(bill)}
                              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 font-medium text-sm"
                            >
                              <Edit className="w-4 h-4" />
                              Edit Appointment
                            </button>

                            {/* Complete and Cancel - Side by Side */}
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                onClick={() => handleCompleteBill(bill)}
                                className="flex items-center justify-center gap-1.5 px-3 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 font-medium text-sm"
                              >
                                <CheckCircle className="w-4 h-4" />
                                Complete
                              </button>
                              <button
                                onClick={() => handleCancelAppointment(bill)}
                                disabled={cancellingBillId === bill._id}
                                className="flex items-center justify-center gap-1.5 px-3 py-2 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-lg hover:from-red-600 hover:to-rose-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {cancellingBillId === bill._id ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>Cancelling...</span>
                                  </>
                                ) : (
                                  <>
                                    <X className="w-4 h-4" />
                                    Cancel
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-gray-100 border-2 border-gray-300 rounded-lg px-4 py-3 text-center">
                            <p className="text-sm text-gray-600 font-medium">This appointment has been cancelled</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>

              {getFilteredPendingBills().length > 6 && !showAllPendingBills && (
                <div className="mt-4 text-center">
                  <button
                    onClick={() => {
                      setShowAllPendingBills(true);
                      // Scroll to pending bills section
                      setTimeout(() => {
                        document.querySelector('.pending-bills-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 100);
                    }}
                    className="w-full flex items-center justify-center gap-2 text-sm text-orange-700 font-semibold bg-orange-100 hover:bg-orange-200 py-3 rounded-lg transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5 border-2 border-orange-200 hover:border-orange-300"
                  >
                    <ArrowUp className="w-4 h-4 rotate-180" />
                    <span>+ {getFilteredPendingBills().length - 6} more pending bills - Click to show all</span>
                    <ArrowUp className="w-4 h-4 rotate-180" />
                  </button>
                </div>
              )}

              {showAllPendingBills && getFilteredPendingBills().length > 6 && (
                <div className="mt-4 text-center space-y-2">
                  <p className="text-sm text-gray-600">
                    Showing all <span className="font-bold text-orange-700">{getFilteredPendingBills().length}</span> pending bills
                  </p>
                  <button
                    onClick={() => {
                      setShowAllPendingBills(false);
                      // Scroll to top of pending bills section
                      document.querySelector('.pending-bills-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="flex items-center justify-center gap-2 px-6 py-2.5 bg-gray-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg hover:bg-gray-700 transition-all transform hover:-translate-y-0.5"
                  >
                    <ArrowUp className="w-4 h-4" />
                    Show Less
                  </button>
                </div>
              )}

              {/* Show message if no bills match filter */}
              {getFilteredPendingBills().length === 0 && showTodayPendingOnly && (
                <div className="text-center py-8">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-gray-700 font-semibold text-lg">No pending appointments for today!</p>
                  <p className="text-gray-500 text-sm mt-1">All appointments scheduled for today are paid</p>
                </div>
              )}
            </div>
          )}

          {/* Recent Bills Table */}
          <div className="bg-white rounded-2xl shadow-xl p-6 backdrop-blur-lg bg-opacity-90 bills-table">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">Recent Transactions</h2>
                <p className="text-sm text-gray-500">Latest bills from your business</p>
              </div>
              <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">{billsData.length} Total</span>
              </div>
            </div>

            {billsData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Bill Number
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[...billsData].reverse().slice(0, billsToShow).map((bill, index) => (
                      <tr key={bill._id || index} className="hover:bg-blue-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="bg-blue-100 p-2 rounded-lg mr-3">
                              <CreditCard className="w-4 h-4 text-blue-600" />
                            </div>
                            <span className="text-sm font-bold text-gray-900">{bill.billNumber || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="bg-purple-100 p-2 rounded-full mr-3">
                              <Users className="w-4 h-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{bill.client?.name || 'N/A'}</p>
                              <p className="text-xs text-gray-500">{bill.client?.phoneNumber || ''}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-700">
                            <p className="font-medium">{new Date(bill.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                            <p className="text-xs text-gray-500">{new Date(bill.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-lg font-bold text-gray-900">
                            {formatCurrency(bill.calculations?.totals?.grandTotal || 0)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1.5 inline-flex items-center gap-1.5 text-xs font-bold rounded-full ${bill.payment?.paymentStatus === 'Paid'
                            ? 'bg-green-100 text-green-700'
                            : bill.payment?.paymentStatus === 'Partial'
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-red-100 text-red-700'
                            }`}>
                            {bill.payment?.paymentStatus === 'Paid' ? (
                              <CheckCircle className="w-3 h-3" />
                            ) : (
                              <AlertCircle className="w-3 h-3" />
                            )}
                            {bill.payment?.paymentStatus || 'Unpaid'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {billsData.length > billsToShow && (
                  <div className="mt-6 text-center">
                    <div className="flex items-center justify-center gap-4">
                      <p className="text-sm text-gray-500">
                        Showing <span className="font-bold text-gray-700">{billsToShow}</span> of <span className="font-bold text-gray-700">{billsData.length}</span> total bills
                      </p>
                      <button
                        onClick={handleShowMore}
                        className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
                      >
                        Show More
                      </button>
                    </div>
                  </div>
                )}
                {billsData.length <= billsToShow && billsData.length > 10 && (
                  <div className="mt-6 text-center">
                    <div className="flex items-center justify-center gap-4">
                      <p className="text-sm text-gray-500">
                        Showing all <span className="font-bold text-gray-700">{billsData.length}</span> bills
                      </p>
                      <button
                        onClick={handleShowLess}
                        className="px-6 py-2 bg-gray-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg hover:bg-gray-700 transition-all"
                      >
                        Show Less
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShoppingBag className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-gray-500 text-lg font-medium">No bills found for the selected period</p>
                <p className="text-gray-400 text-sm mt-1">Try selecting a different date range</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Dashboard;
